import { AlertDialog, Button } from "@pej/kore/components";
import type { AssetFolderSummary } from "./asset-types";

export interface FolderDeleteConfirmProps {
    folder: AssetFolderSummary | null;
    /** null while the count is still being fetched. */
    assetCount: number | null;
    isPending: boolean;
    onConfirm: () => void;
    onCancel: () => void;
}

/**
 * The confirmation before a folder is deleted (spec §5.6). Deleting a folder
 * deletes the assets in it — the API cascades unconditionally — so the whole
 * safety burden falls here.
 *
 * It states three things and no more than it knows: how many assets go with the
 * folder; that the number is what gets *deleted*, not what gets *broken*, since
 * there is no usage index and nothing can compute the blast radius; and the
 * alternative, because there is one and it is not obvious — to keep the files,
 * move them out first.
 *
 * No type-to-echo field, for the same reason as AssetDeleteConfirm: the risk is
 * not knowing what references these assets, and typing a folder name back does
 * not address that.
 *
 * Deliberately does not name whether the in-use asset (spec §3.1) is among
 * what a cascade would take, unlike its sibling AssetDeleteConfirm, which can
 * verify that one usage directly. This dialog would need the picker's
 * `inUseAssetId` plus a folder-scoped page of results to even attempt a
 * best-effort check, and "is it on page one" is a materially weaker
 * guarantee than the sibling's — carried as-is rather than added half-built
 * (C4/M8, final review).
 */
export function FolderDeleteConfirm({ folder, assetCount, isPending, onConfirm, onCancel }: FolderDeleteConfirmProps) {
    if (!folder) return null;

    const counted = assetCount !== null;
    const empty = assetCount === 0;

    return (
        <AlertDialog
            open
            onOpenChange={(open) => {
                // Backdrop click or Escape while the delete is in flight must not
                // fire onCancel — the mutation is already committed to the server,
                // so "cancelling" now would only desync the caller from what
                // actually happened.
                if (!open && !isPending) onCancel();
            }}
        >
            <AlertDialog.Content>
                <AlertDialog.Header>
                    <AlertDialog.Title>Delete "{folder.name}"?</AlertDialog.Title>
                    <AlertDialog.Description>
                        <span className="block">
                            {!counted && "Counting what is in this folder…"}
                            {counted && empty && "This folder is empty."}
                            {counted &&
                                !empty &&
                                `Deleting this folder also deletes the ${assetCount} file${assetCount === 1 ? "" : "s"} in it. To keep them, cancel and move them out first.`}
                        </span>
                        <span className="mt-2 block">
                            Other uses of those files cannot be checked — nothing tracks which templates reference them.
                            This cannot be undone.
                        </span>
                    </AlertDialog.Description>
                </AlertDialog.Header>
                <AlertDialog.Footer>
                    <Button type="button" variant="outline" onClick={onCancel} disabled={isPending}>
                        Cancel
                    </Button>
                    <Button
                        type="button"
                        variant="destructive-solid"
                        onClick={onConfirm}
                        disabled={isPending || !counted}
                    >
                        {isPending ? "Deleting…" : "Delete folder"}
                    </Button>
                </AlertDialog.Footer>
            </AlertDialog.Content>
        </AlertDialog>
    );
}
